'use client';

import Paginator from '@/layers/presentation/components/common/Paginator';
import PatientsList from '@/layers/presentation/components/patients/PatientsList';
import ShowParams from '@/layers/presentation/components/patients/ShowParams';
import { Card, CardBody } from '@/layers/presentation/components/ui/Card';
import MarkdownRenderer from '@/layers/presentation/components/utils/MarkdownRenderer';
import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useTranslation } from '@/layers/presentation/i18n/client';
import logger from '@/layers/support/monitoring/logger';
import { ProgramStatus, UserType } from '@/types/modelEnums';
import { PatientParamValues, Program, ProgramLocation, SponsorTrial } from '@/types/models';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  Skeleton,
  useDisclosure,
} from '@nextui-org/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '../ui/Table';
import ProgramTags from './ProgramTags';
import SaveProgramButton from './SaveProgramButton';

const LOG_MODULE = 'FE [Presentation / Components / ProgramDetails]';

interface ProgramDetailsProps {
  program: Program | null;
  isLoading: boolean;
  type: UserType;
  returnPath?: string;
}

const ProgramDetails: React.FC<ProgramDetailsProps> = ({
  program,
  isLoading,
  returnPath = '/patient/programs/',
  type,
}) => {
  const { t } = useTranslation();

  // Read `out_of_range` from the URL after mount rather than with useSearchParams().
  // The pages that render this component are cached (they export `revalidate`), so
  // their render is a prerender, and useSearchParams() bails out to client rendering
  // there. That bailout throws unless a Suspense boundary catches it — and wrapping
  // this component in one would keep the program body out of the server-rendered
  // HTML, which is exactly what must not happen on these pages. Reading the value in
  // an effect keeps the body server-rendered and removes the bailout entirely.
  const [isOutOfRange, setIsOutOfRange] = useState(false);

  useEffect(() => {
    setIsOutOfRange(new URLSearchParams(window.location.search).get('out_of_range') === 'true');
  }, []);

  const { profile } = useGlobalState();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const filteredLocations = useMemo(() => {
    if (!program?.meta?.locations) return [];
    return program.meta.locations.filter(
      (location: ProgramLocation) => location.country === 'United States',
    );
  }, [program?.meta?.locations]);

  // Logged from an effect, not during render: this component is server-rendered, and
  // a render-phase call would serialise the whole program object into the server log
  // on every cached render.
  useEffect(() => {
    logger.info(`${LOG_MODULE} Render`, type, program?.id, isLoading);
  }, [type, program?.id, isLoading]);

  if (isLoading) {
    return (
      <>
        <Skeleton className="rounded-xl w-3/4 h-14 mb-10" />
        <Skeleton className="rounded-xl w-1/2 h-12 mb-6" />
        <Skeleton className="rounded-xl w-full h-12" />
      </>
    );
  }

  if (!program) {
    return <p>{t('components:programDetails.noProgramDetails')}</p>;
  }

  // Show patient friendly texts if present
  if (type == UserType.PATIENT) {
    program.name = program.details?.patient_friendly?.title || program.name;
    program.lead = program.details?.patient_friendly?.lead || program.lead;
    program.overview = program.details?.patient_friendly?.overview || program.overview;
    program.details!.description =
      program.details?.patient_friendly?.description || program.details?.description;
    program.details!.eligibility.inclusion_criteria =
      program.details?.patient_friendly?.inclusion_criteria ||
      program.details?.eligibility.inclusion_criteria;
    program.details!.eligibility.exclusion_criteria =
      program.details?.patient_friendly?.exclusion_criteria ||
      program.details?.eligibility.exclusion_criteria;
  }

  const isShowEaInfo =
    program.details?.study_type !== 'EXPANDED_ACCESS' &&
    program.sponsor_trials?.results &&
    program.sponsor_trials.results.length > 0;

  const showMatchingPatients =
    type === UserType.PROVIDER &&
    profile?.type === UserType.PROVIDER &&
    program.status === ProgramStatus.ACTIVE;

  const isShowLocations =
    program.status === ProgramStatus.ACTIVE &&
    program.meta &&
    program.meta.locations &&
    filteredLocations.length > 0;

  const isShowSwitchViewButton =
    type === UserType.PATIENT ||
    (type === UserType.PROVIDER && !profile && program.status !== ProgramStatus.ACTIVE);

  const isShowSaveButton =
    (type === UserType.PROVIDER && profile?.type === UserType.PROVIDER) ||
    (type === UserType.PATIENT && profile?.type === UserType.PATIENT);

  return (
    <>
      {program && program.details && (
        <div className="flex flex-row justify-between align-middle flex-wrap sm:flex-nowrap my-8 sm:my-0">
          <div className="flex flex-col gap-2 mb-4 sm:mb-16">
            <div className="flex flex-row items-center gap-3">
              <p className="m-0">
                {t('components:programDetails.sponsor')}:{' '}
                <strong>{program.details.sponsor_name}</strong>
              </p>
              <ProgramTags
                program={program}
                type={type}
                isOutOfRange={isOutOfRange}
                isShowEaInfo={isShowEaInfo}
                isInactive={program.status !== ProgramStatus.ACTIVE}
              />
            </div>
            <p>
              {t('components:programDetails.clinicalTrialsId')}:
              <Link
                href={`https://clinicaltrials.gov/ct2/show/${program.details.nct_id}`}
                target="_blank"
                className="text-xl ml-1"
              >
                {program.details.nct_id}
              </Link>
            </p>
          </div>
          <div className="flex flex-row gap-4 items-center">
            {isShowSaveButton && (
              <SaveProgramButton programId={program.id} size="lg" variant="bordered" />
            )}
            {isShowSwitchViewButton && (
              <Button
                as={Link}
                size="lg"
                radius="md"
                color="primary"
                href={`/${type === UserType.PATIENT ? 'provider' : 'patient'}/programs/${program.id}`}
              >
                {type === UserType.PATIENT ? 'Provider View' : 'Patient View'}
              </Button>
            )}
          </div>
        </div>
      )}

      {program.lead && <p className="text-xl sm:text-2xl mb-16">{program.lead}</p>}

      <h2>{t('components:programDetails.patientParameters')}</h2>
      <ShowParams type="view" params={program.patient_params as PatientParamValues} />

      {showMatchingPatients && (
        <>
          <div className="flex flex-row justify-between">
            <h3 className="mt-12">{t('components:programDetails.yourPatients')}</h3>
          </div>

          <PatientsList type="match" filters={{ provider_id: [profile?.id] }} program={program} />

          <div className="flex justify-end mt-4">
            <Button
              as={Link}
              href={`/provider/patients/add/?programId=${program.id}&params=${
                program.patient_params
                  ? encodeURIComponent(JSON.stringify(program.patient_params))
                  : ''
              }`}
              size="sm"
              color="primary"
              radius="sm"
            >
              {t('components:programDetails.addNewPatient')}
            </Button>
          </div>
        </>
      )}

      <div className="program-details">
        {program?.overview && (
          <>
            <h2 className="mt-12 mb-0 sm:mt-16 sm:mb-4">
              {t('components:programDetails.programOverview')}
            </h2>
            <MarkdownRenderer content={program.overview} />
          </>
        )}

        {program && program.details && program.details.description && (
          <>
            <h2 className="mt-12 mb-0 sm:mt-16 sm:mb-4">
              {t('components:programDetails.description')}
            </h2>
            <MarkdownRenderer content={program.details.description} />
          </>
        )}

        {program && program.details && program.details.eligibility && (
          <>
            <h2 className="mt-12 mb-0 sm:mt-16 sm:mb-4">
              {t('components:programDetails.eligibilityCriteria')}
            </h2>
            <Card
              classNames={{
                base: ['shadow-none', 'border-1', 'p-2 sm:p-6'],
              }}
            >
              <CardBody>
                {program.details.eligibility.general_criteria && (
                  <MarkdownRenderer content={program.details.eligibility.general_criteria} />
                )}
                {program.details.eligibility.inclusion_criteria && (
                  <>
                    <h4 className="mb-0">{t('components:programDetails.inclusionCriteria')}</h4>
                    <MarkdownRenderer content={program.details.eligibility.inclusion_criteria} />
                  </>
                )}
                {program.details.eligibility.exclusion_criteria && (
                  <>
                    <h4 className="mt-8 first:mt-0 mb-0">
                      {t('components:programDetails.exclusionCriteria')}
                    </h4>
                    <MarkdownRenderer content={program.details.eligibility.exclusion_criteria} />
                  </>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {isShowLocations && (
          <>
            <h2 className="mt-12 mb-0 sm:mt-16 sm:mb-4">Locations</h2>
            <Paginator<ProgramLocation> list={filteredLocations} pageSize={10}>
              {paginatedItems => (
                <Table aria-label="Locations">
                  <TableHeader>
                    <TableColumn>FACILITY</TableColumn>
                    <TableColumn>ZIP</TableColumn>
                    <TableColumn>CITY</TableColumn>
                    <TableColumn>STATE</TableColumn>
                  </TableHeader>
                  <TableBody emptyContent={t('components:showParams.noInformation')}>
                    {paginatedItems.map((item: ProgramLocation) => (
                      <TableRow key={item.zip}>
                        <TableCell>{item.facility}</TableCell>
                        <TableCell>{item.zip}</TableCell>
                        <TableCell>{item.city}</TableCell>
                        <TableCell>{item.state}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paginator>
          </>
        )}

        {isShowEaInfo && (
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="5xl"
            classNames={{
              base: 'rounded-2xl',
              body: 'p-10',
            }}
          >
            <ModalContent>
              <ModalBody>
                <h2 className="m-0">Expanded Access Information</h2>
                <p>
                  <strong>{program.details?.sponsor_name}</strong> provided expanded access programs
                  to their trials before.
                </p>
                <p className="mb-8">
                  This isn&apos;t a guarantee that they will provide expanded access to selected
                  trial, but it is possible.
                </p>
                <h3>Programs from {program.details?.sponsor_name}</h3>
                <Paginator<SponsorTrial> list={program.sponsor_trials?.results ?? []} pageSize={5}>
                  {paginatedItems => (
                    <Table
                      aria-label="Sponsor Trials"
                      classNames={{
                        base: 'min-h-[330px] max-h-[300px] overflow-auto border-1 rounded-xl',
                      }}
                    >
                      <TableHeader>
                        <TableColumn>TITLE</TableColumn>
                        <TableColumn>SOURCE</TableColumn>
                      </TableHeader>
                      <TableBody emptyContent={t('components:showParams.noInformation')}>
                        {paginatedItems.map((item: SponsorTrial) => (
                          <TableRow key={item.title}>
                            <TableCell>{item.title}</TableCell>
                            <TableCell>{item.link}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paginator>
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </div>
    </>
  );
};

export default ProgramDetails;
